import React from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import 'components/UI/Form.css';
import { useGetObjectiveEntriesQuery } from 'services/objectives/api';
import { Objective } from 'services/objectives/types';
import { isDueDatePast, toDateTime, userNow } from 'utils/date';
import { getEstimatedCompletionDate, goalKindOf } from '../ObjectivesViewer/utils';

const ObjectiveStats = ({
  objective,
}: {
  objective: Objective;
}) => {
  const { t } = useTranslation();
  const { data: entries = [] } = useGetObjectiveEntriesQuery(objective.id, {
    skip: !objective?.id,
  });

  const latestEntry = entries[0];
  const today = userNow();
  const kind = goalKindOf(objective);
  const { goal } = objective;
  const lastProgressEventDate = latestEntry?.recordedAt
    ? DateTime.fromISO(latestEntry.recordedAt)
    : null;
  const timeSinceLastProgressEvent = lastProgressEventDate?.isValid
    ? today.diff(lastProgressEventDate, 'days').days
    : null;
  const dueDate = toDateTime(objective.dueDate);
  const daysLeft = dueDate?.isValid
    ? dueDate.setZone(today.zone).startOf('day').diff(today.startOf('day'), 'days').days
    : null;
  const currentProgress = objective.progress || 0;
  const completionRatio = goal ? (currentProgress / goal) : null;
  const estimatedCompletionDate = getEstimatedCompletionDate(objective);
  const isLate = kind === 'REACH' && currentProgress < 1 && isDueDatePast(objective.dueDate, today);
  const isEstimatedLate = !isLate && !!estimatedCompletionDate && !!dueDate?.isValid && (estimatedCompletionDate > dueDate);
  const showReachStats = kind === 'REACH' && goal != null;

  return (
    <table>
      <tbody>
        <tr>
          <td
            style={{
              verticalAlign: 'top',
            }}
          >
            <div>
              <b>{t('lastUpdate')}</b>
              <br />
              Il y a { timeSinceLastProgressEvent != null ? Math.round(timeSinceLastProgressEvent) : '-' } jours
            </div>
            {
              showReachStats && (
                <div>
                  <b>Pourcentage de complétion</b>
                  <br />
                  { completionRatio != null ? Math.round(100 * completionRatio) : '-' }%
                </div>
              )
            }
            {
              !showReachStats && (
                <div>
                  <b>{t('currentValue')}</b>
                  <br />
                  {latestEntry ? latestEntry.value : (objective.progress ?? '-')}
                </div>
              )
            }
            <div>
              <b>{t('timeLeft')}</b>
              <br />
              <div
                style={{
                  ...(daysLeft != null && daysLeft < 0 && { color: '#ff0000' }),
                }}
              >
                { daysLeft != null ? Math.round(daysLeft) : '-' } jours
              </div>
            </div>
            {
              showReachStats && (
                <>
                  <div>
                    <b>{t('estimatedCompletionDate')}</b>
                    <br />
                    <div
                      style={{
                        ...(isLate && { color: '#ff0000' }),
                        ...(isEstimatedLate && { color: '#dfa901' }),
                      }}
                    >
                      { estimatedCompletionDate ? estimatedCompletionDate.toFormat('yyyy-MM-dd') : '-' }
                    </div>
                  </div>
                  <div>
                    <b>{t('requiredPace')}</b>
                    <br />
                    <div>
                      { daysLeft ? Math.round(((goal || 0) - currentProgress) / daysLeft) : '-' } / jour
                    </div>
                  </div>
                </>
              )
            }
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default ObjectiveStats;
